import React, { useState } from 'react';
import './Products.css';
import SideBar from '../SideBar/SideBar';

interface Product {
  id: number;
  name: string;
  price: number;
  category: string;
  stock: number;
  description: string;
  image: string | null; // URL or base64
}

const Products: React.FC = () => {
    const [categories, setCategories] = useState<string[]>([
        'Styling', 'Hair Care', 'Beard', 'Accessories'
    ]);
  const [products, setProducts] = useState<Product[]>([
    { id: 1, name: 'Matte Pomade', price: 18.00, category: 'Styling', stock: 50, description: 'High hold, matte finish pomade.', image: null },
    { id: 2, name: 'Shaping Clay', price: 20.00, category: 'Styling', stock: 30, description: 'Flexible hold clay.', image: null },
    { id: 3, name: 'Argan Oil Shampoo', price: 24.00, category: 'Hair Care', stock: 15, description: 'Nourishing shampoo.', image: null },
    { id: 4, name: 'Beard Oil', price: 15.00, category: 'Beard', stock: 100, description: 'Softens and conditions beard.', image: null },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Partial<Product>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Category Management State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [tempCategoryName, setTempCategoryName] = useState('');
  
  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = products.filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
      setCurrentProduct({});
      setImagePreview(null);
      setShowModal(true);
  };

  const handleDelete = (id: number) => {
      if(window.confirm('Είστε σίγουροι για τη διαγραφή του προϊόντος;')) {
          setProducts(products.filter(p => p.id !== id));
      }
  };

  const handleEdit = (product: Product) => {
      setCurrentProduct(product);
      setImagePreview(product.image);
      setShowModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onloadend = () => {
              const result = reader.result as string;
              setImagePreview(result);
              setCurrentProduct(prev => ({ ...prev, image: result }));
          };
          reader.readAsDataURL(file);
      }
  };

  const handleSave = (e: React.FormEvent) => {
      e.preventDefault();
      // Ensure numeric values are numbers
      const productToSave = {
          ...currentProduct,
          price: Number(currentProduct.price),
          stock: Number(currentProduct.stock),
      };

      if (currentProduct.id) {
          // Edit
          setProducts(products.map(p => p.id === currentProduct.id ? productToSave as Product : p));
      } else {
          // Add
          const newProduct = { ...productToSave, id: products.length + 1 } as Product;
          setProducts([...products, newProduct]);
      }
      setShowModal(false);
  };

  const handleAddCategory = () => {
      if (newCategoryName.trim() && !categories.includes(newCategoryName.trim())) {
          setCategories([...categories, newCategoryName.trim()]);
          setNewCategoryName('');
      } else if (categories.includes(newCategoryName.trim())) {
          alert('Η κατηγορία υπάρχει ήδη.');
      }
  };

  const handleStartEditCategory = (category: string) => {
      setEditingCategory(category);
      setTempCategoryName(category);
  };

  const handleSaveCategoryEdit = () => {
      if (tempCategoryName.trim() && editingCategory) {
          if (categories.includes(tempCategoryName.trim()) && tempCategoryName.trim() !== editingCategory) {
               alert('Η κατηγορία υπάρχει ήδη.');
               return;
          }
          
          setCategories(categories.map(c => c === editingCategory ? tempCategoryName.trim() : c));
          setProducts(products.map(p => p.category === editingCategory ? { ...p, category: tempCategoryName.trim() } : p));
          
          setEditingCategory(null);
          setTempCategoryName('');
      }
  };

  const handleCancelCategoryEdit = () => {
      setEditingCategory(null);
      setTempCategoryName('');
  };

  const handleDeleteCategory = (category: string) => {
      if (window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε την κατηγορία "${category}"; (Τα προϊόντα δεν θα επηρεαστούν)`)) {
          setCategories(categories.filter(c => c !== category));
      }
  };

  return (
    <div className="admin-container">
      <SideBar />
      <div className="admin-content">
        <div className="page-header">
            <h2>Διαχείριση Προϊόντων</h2>
            
            <div className="search-wrapper">
                <input 
                    type="text" 
                    placeholder="Αναζήτηση προϊόντων..." 
                    className="product-search-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="search-icon">🔍</span>
            </div>

            <div style={{display:'flex', gap:'10px'}}>
                <button className="add-btn" style={{backgroundColor:'#6c757d', backgroundImage: 'none'}} onClick={() => setShowCategoryModal(true)}>
                     ⚙ Κατηγορίες
                </button>
                <button className="add-btn" onClick={handleAddClick}>+ Νέο Προϊόν</button>
            </div>
        </div>
        
        <div className="products-grid">
            {filteredProducts.map((product) => (
                <div key={product.id} className="product-card">
                    <div className="product-image-area">
                        {product.image ? (
                            <img src={product.image} alt={product.name} className="product-card-img" />
                        ) : (
                            <div className="product-card-no-img">
                                <span>No Image</span>
                            </div>
                        )}
                        <span className="product-id-badge">#{product.id}</span>
                        <div className="product-overlay-actions">
                            <button className="action-circle-btn edit" onClick={() => handleEdit(product)} title="Επεξεργασία">
                                ✎
                            </button>
                            <button className="action-circle-btn delete" onClick={() => handleDelete(product.id)} title="Διαγραφή">
                                🗑
                            </button>
                        </div>
                    </div>
                    
                    <div className="product-card-content">
                        <div className="product-category-row">
                             <span className="product-category">{product.category}</span>
                        </div>
                        
                        <h3 className="product-title">{product.name}</h3>
                        <p className="product-description">{product.description || 'Χωρίς περιγραφή'}</p>
                        
                        <div className="product-footer-row">
                            <div className="price-tag">
                                {product.price.toFixed(2)}€
                            </div>
                            <span className={`stock-badge ${product.stock < 20 ? 'low' : 'ok'}`}>
                                {product.stock} τμχ.
                            </span>
                        </div>
                    </div>
                </div>
            ))}
        </div>

        {showModal && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>{currentProduct.id ? 'Επεξεργασία Προϊόντος' : 'Νέο Προϊόν'}</h3>
                    </div>
                    
                    <div className="modal-body">
                        <form onSubmit={handleSave} id="productForm">
                             <div className="form-group">
                                <label>Εικόνα Προϊόντος:</label>
                                <div className="image-preview-container" onClick={() => document.getElementById('fileInput')?.click()}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="image-preview" />
                                    ) : (
                                        <div className="upload-placeholder">
                                            <span>Κάντε κλικ για ανέβασμα εικόνας</span>
                                        </div>
                                    )}
                                    <input 
                                        type="file" 
                                        id="fileInput" 
                                        accept="image/*" 
                                        onChange={handleImageChange} 
                                        style={{display: 'none'}} 
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Όνομα:</label>
                                <input 
                                    type="text" 
                                    value={currentProduct.name || ''} 
                                    onChange={e => setCurrentProduct({...currentProduct, name: e.target.value})}
                                    required
                                    placeholder="π.χ. Matte Pomade"
                                />
                            </div>

                             <div className="form-group">
                                <label>Περιγραφή:</label>
                                <textarea 
                                    value={currentProduct.description || ''} 
                                    onChange={e => setCurrentProduct({...currentProduct, description: e.target.value})}
                                    placeholder="Περιγραφή προϊόντος..."
                                />
                            </div>

                            <div style={{display:'flex', gap:'15px'}}>
                                <div className="form-group" style={{flex: 1}}>
                                    <label>Κατηγορία:</label>
                                    <select 
                                        value={currentProduct.category || ''} 
                                        onChange={e => setCurrentProduct({...currentProduct, category: e.target.value})}
                                        required
                                    >
                                        <option value="">Επιλέξτε...</option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group" style={{flex: 1}}>
                                    <label>Τιμή (€):</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        min="0"
                                        value={currentProduct.price || ''} 
                                        onChange={e => setCurrentProduct({...currentProduct, price: parseFloat(e.target.value)})}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{flex: 1}}>
                                    <label>Απόθεμα:</label>
                                    <input 
                                        type="number" 
                                        min="0"
                                        value={currentProduct.stock || ''} 
                                        onChange={e => setCurrentProduct({...currentProduct, stock: parseInt(e.target.value)})}
                                        required
                                    />
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={() => setShowModal(false)} className="cancel-btn">Ακύρωση</button>
                        <button type="submit" form="productForm" className="save-btn">Αποθήκευση</button>
                    </div>
                </div>
            </div>
        )}

        {/* Categories Modal */}
        {showCategoryModal && (
            <div className="modal-overlay">
                <div className="modal-content" style={{maxWidth: '400px'}}>
                    <div className="modal-header">
                        <h3>Διαχείριση Κατηγοριών</h3>
                        <button className="close-btn" onClick={() => setShowCategoryModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                         <div style={{display:'flex', gap:'10px', marginBottom:'20px'}}>
                            <input 
                                type="text" 
                                placeholder="Νέα Κατηγορία" 
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                style={{flex: 1, padding:'8px', borderRadius:'4px', border:'1px solid #ddd'}}
                            />
                            <button 
                                onClick={handleAddCategory}
                                style={{
                                    backgroundColor: '#28a745', 
                                    color:'white', 
                                    border:'none', 
                                    padding:'0 15px', 
                                    borderRadius:'4px',
                                    cursor:'pointer'
                                }}
                            >
                                +
                            </button>
                         </div>
                         
                        <ul style={{listStyle:'none', padding:0, margin:0, maxHeight:'300px', overflowY:'auto'}}>
                             {categories.map(cat => (
                                 <li key={cat} style={{
                                     padding:'10px', 
                                     borderBottom:'1px solid #eee', 
                                     display:'flex', 
                                     justifyContent:'space-between',
                                     alignItems:'center'
                                 }}>
                                    {editingCategory === cat ? (
                                        <div style={{display:'flex', gap:'5px', width: '100%'}}>
                                            <input 
                                                type="text" 
                                                value={tempCategoryName}
                                                onChange={(e) => setTempCategoryName(e.target.value)}
                                                style={{flex: 1, padding:'4px', borderRadius:'4px', border:'1px solid #ccc'}}
                                            />
                                            <button onClick={handleSaveCategoryEdit} style={{cursor:'pointer', color:'#28a745', border:'none', background:'none'}}>✔</button>
                                            <button onClick={handleCancelCategoryEdit} style={{cursor:'pointer', color:'#dc3545', border:'none', background:'none'}}>✘</button>
                                        </div>
                                    ) : (
                                        <>
                                            <span>{cat}</span>
                                            <div style={{display:'flex', gap:'5px'}}>
                                                <button
                                                    onClick={() => handleStartEditCategory(cat)}
                                                    style={{
                                                        background:'none',
                                                        border:'none',
                                                        color:'#007bff',
                                                        cursor:'pointer',
                                                        fontSize:'16px'
                                                    }}
                                                    title="Edit Category"
                                                >
                                                    ✎
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteCategory(cat)}
                                                    style={{
                                                        background:'none',
                                                        border:'none',
                                                        color:'#dc3545',
                                                        cursor:'pointer',
                                                        fontSize:'18px'
                                                    }}
                                                    title="Delete Category"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        </>
                                    )}
                                 </li>
                             ))}
                         </ul>
                         {categories.length === 0 && <p style={{color:'#999', textAlign:'center'}}>Δεν υπάρχουν κατηγορίες.</p>}
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default Products;
